import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useGetOne, useTranslate } from 'react-admin';
import { Box, CircularProgress, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { DeviceConfigCopyButton } from '@/components/device/config/DeviceConfigCopyButton.jsx';
import { DeviceConfigDeviceSelect } from '@/components/device/config/DeviceConfigDeviceSelect.jsx';
import { DeviceConfigSummary } from '@/components/device/config/DeviceConfigSummary.jsx';
import { DeviceConfigTable } from '@/components/device/config/DeviceConfigTable.jsx';
import { getDeviceConfigSummary, normalizeDeviceConfigRecord } from '@/helpers/device/deviceConfig.js';
import { deviceConfigStyles } from '@/themes/commonStyles';

const StyledDeviceConfig = styled(Box)(deviceConfigStyles);

export const DeviceConfigContent = ({ deviceId: initialDeviceId }) => {
    const translate = useTranslate();
    const [deviceId, setDeviceId] = useState(initialDeviceId ?? null);

    useEffect(() => {
        setDeviceId(initialDeviceId ?? null);
    }, [initialDeviceId]);

    const { data: configData, isPending: isConfigPending, error: configError } = useGetOne('device-config', { id: deviceId }, { enabled: Boolean(deviceId) });
    const { data: deviceData } = useGetOne('device', { id: deviceId }, { enabled: Boolean(deviceId) });
    const record = useMemo(() => normalizeDeviceConfigRecord(configData, deviceId), [configData, deviceId]);
    const summary = useMemo(() => getDeviceConfigSummary(deviceData, configData), [configData, deviceData]);
    const parameters = record.parameters;
    const hasParameters = parameters.length > 0;
    const isLoadingConfig = Boolean(deviceId) && isConfigPending;
    const canShowParameters = Boolean(deviceId) && !isLoadingConfig && !configError;
    const parametersContent = hasParameters ? (
        <DeviceConfigTable parameters={parameters} />
    ) : (
        <Box className='device-config-empty'>
            <Typography variant='body2'>{translate('common.noData')}</Typography>
        </Box>
    );

    return (
        <StyledDeviceConfig>
            <Box className='device-config-toolbar'>
                <DeviceConfigDeviceSelect deviceId={deviceId} onChange={setDeviceId} />
                <DeviceConfigCopyButton parameters={parameters} rawConfig={record.rawConfig} disabled={!hasParameters && !record.rawConfig} />
            </Box>
            {!deviceId ? (
                <Box className='device-config-empty'>
                    <Typography variant='body2'>{translate('resources.device.labels.selectDeviceToShowConfiguration')}</Typography>
                </Box>
            ) : null}
            {deviceId ? <DeviceConfigSummary summary={summary} /> : null}
            {isLoadingConfig ? (
                <Box className='device-config-loading'>
                    <CircularProgress size={24} />
                </Box>
            ) : null}
            {configError ? <Typography variant='body2'>{translate('common.errors.loadingData')}</Typography> : null}
            {canShowParameters ? parametersContent : null}
        </StyledDeviceConfig>
    );
};

DeviceConfigContent.propTypes = {
    deviceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
